/**
 * DecisionGateCard (CHAP-249) — the go/no-go countdown + readiness criteria.
 * Shown on Overview. Days-remaining badge (green >=60 / amber 30-60 / red <30),
 * traffic-light criteria with click-through to the owning tab, team/admin edit,
 * and a computed outlook.
 */
import { Link } from 'react-router-dom';
import { useDecisionGate, useRecordDecision, useUpdateCriterion } from '../hooks/useDecisionGate.js';
import { useUserRoles } from '../hooks/useUserRoles.js';
import type { DecisionGateOutcome, DecisionGateState, DecisionOutlook } from '../../shared/types.js';

const DOT: Record<DecisionGateState, string> = {
  green: 'bg-green-500',
  amber: 'bg-amber-400',
  red: 'bg-red-500',
};

const OUTLOOK: Record<DecisionOutlook, { label: string; cls: string }> = {
  demolition_track: { label: 'Demolition track', cls: 'bg-green-100 text-green-800' },
  rental_renewal: { label: 'Rental renewal', cls: 'bg-red-100 text-red-800' },
  uncertain: { label: 'Uncertain', cls: 'bg-amber-100 text-amber-800' },
};

// Recorded (not computed) outcome labels — reuses OUTLOOK's colors/copy since
// the values overlap, but is keyed by the narrower DecisionGateOutcome type.
const OUTCOME_LABEL: Record<DecisionGateOutcome, string> = {
  demolition_track: OUTLOOK.demolition_track.label,
  rental_renewal: OUTLOOK.rental_renewal.label,
};

function badgeClass(days: number): string {
  if (days >= 60) return 'bg-green-100 text-green-800';
  if (days >= 30) return 'bg-amber-100 text-amber-800';
  return 'bg-red-100 text-red-800';
}

export function DecisionGateCard({ slug }: { slug: string }) {
  const { data } = useDecisionGate(slug);
  const { allowedRoles } = useUserRoles();
  const canEdit = allowedRoles.includes('team') || allowedRoles.includes('admin');
  const update = useUpdateCriterion(slug);
  const recordDecision = useRecordDecision(slug);

  if (!data?.gate) return null;
  const days = data.daysRemaining ?? 0;
  const target = new Date(`${data.gate.targetDate}T00:00:00`);
  const outlook = data.outlook ? OUTLOOK[data.outlook] : null;
  const decidedAt = data.gate.decidedAt ? new Date(data.gate.decidedAt) : null;
  const decidedOutcome = data.gate.decidedOutcome;
  // Archived once a decision has actually been recorded, OR the target date
  // has passed and an outcome exists (belt-and-suspenders — the route always
  // stamps both together, but the client shouldn't assume that never drifts).
  const isArchived = !!decidedAt || (days < 0 && !!decidedOutcome);
  const isPendingOutcome = days < 0 && !decidedOutcome;

  return (
    <section className="bg-white rounded-xl shadow-sm border border-navy/15 p-4 sm:p-6 min-w-0">
      <div className="flex items-start justify-between flex-wrap gap-3 min-w-0">
        <div className="min-w-0">
          <h3 className="text-navy font-bold text-sm sm:text-base">Decision gate: {data.gate.title}</h3>
          <p className="text-neutral text-xs mt-0.5">
            Target {target.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </p>
        </div>
        {isArchived ? (
          <span className="text-sm font-bold px-3 py-1 rounded-full flex-shrink-0 bg-gray-200 text-navy">
            Decision made — {decidedOutcome ? OUTCOME_LABEL[decidedOutcome] : 'archived'}
            {decidedAt
              ? `, ${decidedAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
              : ''}
          </span>
        ) : isPendingOutcome ? (
          <span className="text-sm font-bold px-3 py-1 rounded-full flex-shrink-0 bg-amber-100 text-amber-800">
            Decision window closed — outcome pending
          </span>
        ) : (
          <span className={`text-sm font-bold px-3 py-1 rounded-full flex-shrink-0 ${badgeClass(days)}`}>
            {days >= 0 ? `${days} days remaining` : `${Math.abs(days)} days overdue`}
          </span>
        )}
      </div>

      {isPendingOutcome && canEdit && (
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <span className="text-xs text-neutral">Record outcome:</span>
          <select
            defaultValue=""
            onChange={(e) => {
              const value = e.target.value as DecisionGateOutcome;
              if (value) recordDecision.mutate({ decidedOutcome: value });
            }}
            className="text-xs border border-gray-300 rounded px-1.5 py-0.5"
            aria-label="Record decision outcome"
          >
            <option value="" disabled>
              Select…
            </option>
            <option value="demolition_track">Demolition track</option>
            <option value="rental_renewal">Rental renewal</option>
          </select>
        </div>
      )}

      <ul className="mt-4 space-y-2">
        {data.criteria?.map((c) => (
          <li key={c.id} className="flex items-start gap-2.5 min-w-0">
            <span className={`mt-1.5 w-2.5 h-2.5 rounded-full flex-shrink-0 ${DOT[c.state]}`} aria-label={c.state} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                {c.owningTab ? (
                  <Link to={`/c/${slug}/${c.owningTab}`} className="text-sm text-navy hover:underline truncate">
                    {c.label}
                  </Link>
                ) : (
                  <span className="text-sm text-navy truncate">{c.label}</span>
                )}
                {canEdit && !isArchived && (
                  <select
                    value={c.state}
                    onChange={(e) => update.mutate({ id: c.id, state: e.target.value as DecisionGateState })}
                    className="text-xs border border-gray-300 rounded px-1.5 py-0.5 flex-shrink-0"
                    aria-label={`${c.label} status`}
                  >
                    <option value="green">Green</option>
                    <option value="amber">Amber</option>
                    <option value="red">Red</option>
                  </select>
                )}
                {canEdit && isArchived && (
                  <span
                    className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${DOT[c.state]}`}
                    aria-label={c.state}
                  />
                )}
              </div>
              {canEdit && !isArchived ? (
                <input
                  defaultValue={c.notes ?? ''}
                  placeholder="Notes…"
                  onBlur={(e) => {
                    if (e.target.value !== (c.notes ?? '')) update.mutate({ id: c.id, notes: e.target.value || null });
                  }}
                  className="mt-1 w-full border border-gray-200 rounded px-2 py-1 text-xs text-neutral"
                />
              ) : (
                c.notes && <p className="text-xs text-neutral mt-0.5">{c.notes}</p>
              )}
            </div>
          </li>
        ))}
      </ul>

      {outlook && !isArchived && (
        <div className="mt-4 pt-3 border-t border-gray-100 flex items-center gap-2 flex-wrap">
          <span className="text-xs text-neutral">Current outlook:</span>
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${outlook.cls}`}>{outlook.label}</span>
          {data.counts && (
            <span className="text-[11px] text-neutral">
              ({data.counts.green} green · {data.counts.amber} amber · {data.counts.red} red)
            </span>
          )}
        </div>
      )}
    </section>
  );
}
